import React, { useState, useEffect } from "react";
import { ChartIcon, CommandIcon, FilterIcon, PaperClipIcon, SendIcon, UserAddIcon, UserGroupIcon } from "../../../assets/icon";

// ─── Types ───────────────────────────────────────────────────────────────────

interface TaskCard {
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface QuickChat {
  id: number;
  text: string;
}

// ─── TaskCardItem ─────────────────────────────────────────────────────────────

const TaskCardItem: React.FC<{ task: TaskCard }> = ({ task }) => (
  <div className="bg-[#F3F8F6] gap-2 border-t-4 border-b-4 border-r-2 border-l-2 border-[#D7E7E1] rounded-xl p-3 sm:p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-violet-100 group">
    <div className="flex gap-1 items-center mb-2">
      <span className="text-[#7B7B7B] font-semibold flex items-center group-hover:text-violet-500 transition-colors">
        {task.icon}
      </span>
      <span className="font-semibold text-xs sm:text-sm text-[#0F766E]">{task.title}</span>
    </div>
    <p className="text-[10px] sm:text-xs text-[#0F766E] leading-relaxed">{task.description}</p>
  </div>
);

// ─── AskBar ───────────────────────────────────────────────────────────────────

const AskBar: React.FC = () => {
  const [value, setValue] = useState("");

  const quickTags = [
    "Quick action tags",
    "Quick action tags",
    "Quick action tags",
    "Quick action tags",
    "Quick action tags",
    "Quick action tags",
  ];

  return (
    <div className="mt-auto pt-4 sm:pt-6">
      {/* Input box */}
      <div className="bg-white border border-gray-200 rounded-2xl px-3 sm:px-4 pt-3 pb-2 shadow-sm">
        <textarea
          className="w-full border-none h-[80px] sm:h-[100px] outline-none resize-none text-xs sm:text-sm text-gray-700 bg-transparent leading-relaxed placeholder-gray-400"
          placeholder="Ask anything."
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="flex items-center justify-between border-gray-100">
          <button className="flex items-center gap-1 sm:gap-1.5 text-[#3A3428] text-[10px] sm:text-[12px] bg-transparent border-none cursor-pointer hover:text-gray-600 transition-colors p-0">
            <PaperClipIcon />
            <span className="hidden xs:inline">Attach Files</span>
          </button>
          <button className="flex items-center gap-1 sm:gap-2 text-[#3A3428] text-[10px] sm:text-[12px] font-medium bg-transparent border-none cursor-pointer p-0 hover:text-gray-900 transition-colors">
            <span className="hidden xs:inline">Send</span>
            <SendIcon />
          </button>
        </div>
      </div>

      {/* Quick action tags */}
      <div className="flex gap-1.5 sm:gap-2 mt-2 flex-wrap">
        {quickTags.map((tag, i) => (
          <button
            key={i}
            className="bg-gray-900 text-white border-none rounded-full px-2.5 sm:px-3.5 py-1 text-[10px] sm:text-xs font-medium cursor-pointer hover:bg-gray-700 transition-colors whitespace-nowrap"
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── GetStartedPanel ──────────────────────────────────────────────────────────

const GetStartedPanel: React.FC = () => {
  const topTasks: TaskCard[] = [
    {
      title: "Task Title 1",
      description: "Connect WhatsApp, Instagram, LinkedIn, and more to manage all conversations in one place.",
      icon: <CommandIcon />,
    },
    {
      title: "Task Title 2",
      description: "Invite teammates and assign roles to collaborate efficiently.",
      icon: <UserAddIcon />,
    },
  ];

  const bottomTasks: TaskCard[] = [
    {
      title: "Assign Conversations",
      description: "Easily assign conversations to team members to streamline engagements.",
      icon: <UserGroupIcon />,
    },
    {
      title: "Track and Update Leads",
      description: "Monitor lead status and move prospects through your pipeline.",
      icon: <FilterIcon />,
    },
    {
      title: "Track Insights",
      description: "Analyse what’s driving engagement and sales. Use data to convert leads into deals.",
      icon: <ChartIcon />,
    },
  ];

  return (
    <div className="flex-1 flex flex-col gap-3 sm:gap-3.5">
      {/* Header */}
      <div className="text-center pb-1 px-2">
        <h1 className="text-xl sm:text-2xl lg:text-[24px] text-[#3A3428] mb-1.5 tracking-tight">
          Get Started with <span className="hidden xs:inline">ConvoHub</span>
          <span className="text-[#000] font-bold">CRM</span>
        </h1>
        <p className="text-[10px] sm:text-[12px] text-[#434343] leading-relaxed">
          <span className="hidden xs:inline">
            Connect channels, invite teammates, organise customers,
            <br />
            and manage conversations efficiently.
          </span>
          <span className="xs:hidden">
            Connect channels, invite teammates, and manage conversations.
          </span>
        </p>
      </div>

      {/* Top row - becomes 1 column on very small screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3">
        {topTasks.map((task, i) => (
          <TaskCardItem key={i} task={task} />
        ))}
      </div>

      {/* Bottom row - becomes 1 column on small screens, then 2, then 3 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
        {bottomTasks.map((task, i) => (
          <TaskCardItem key={i} task={task} />
        ))}
      </div>

      <AskBar />
    </div>
  );
};

// ─── RecentChats ──────────────────────────────────────────────────────────────

const RecentChats: React.FC = () => {
  const [activeId, setActiveId] = useState<number>(1);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const chats: QuickChat[] = [
    { id: 1, text: "How to quickly add team members" },
    { id: 2, text: "How to quickly add team members" },
    { id: 3, text: "How to quickly add team members" },
    { id: 4, text: "How to quickly add team members" },
    { id: 5, text: "How to quickly add team members" },
  ];

  // Mobile version - collapsible sidebar
  if (isMobile) {
    return (
      <>
        {/* Toggle button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="fixed bottom-4 right-4 bg-gray-900 text-white p-3 rounded-full shadow-lg z-50 md:hidden"
          aria-label="Toggle recent chats"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>

        {/* Slide-over panel */}
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity md:hidden ${
            isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setIsOpen(false)}
        >
          <aside
            className={`absolute right-0 top-0 h-full w-64 bg-white shadow-xl transform transition-transform ${
              isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-medium text-black">Recent Chats</h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex flex-col p-2">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  onClick={() => {
                    setActiveId(chat.id);
                    setIsOpen(false);
                  }}
                  className={`px-4 py-3 text-sm leading-snug cursor-pointer transition-colors rounded-lg ${
                    activeId === chat.id
                      ? "bg-[#EAEDFB] text-[#3A3428] font-medium border-2 border-[#9197B3]"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {chat.text}
                </div>
              ))}
            </div>
          </aside>
        </div>
      </>
    );
  }

  // Desktop version - static sidebar
  return (
    <aside className="hidden md:block w-60 min-w-[220px] bg-white border border-gray-200 rounded-xl py-4 shrink-0 self-start">
      <h2 className="text-[20px] font-medium text-black mb-6 text-center bg-[#FCF6F9] p-2 mx-3 rounded-xl border-b border-gray-200">
        Recent Chats
      </h2>
      <div className="flex flex-col mt-1">
        {chats.map((chat) => (
          <div
            key={chat.id}
            onClick={() => setActiveId(chat.id)}
            className={`px-4 py-2.5 mx-3 text-xs leading-snug cursor-pointer transition-colors ${
              activeId === chat.id
                ? "bg-[#EAEDFB] text-[#3A3428] font-medium border-2 border-[#9197B3] rounded-xl"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {chat.text}
          </div>
        ))}
      </div>
    </aside>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────

const ConvoAi: React.FC = () => {
  return (
    <>
      <div className="font-sans bg-gray-50 min-h-screen flex flex-col">
        <main className="flex flex-col md:flex-row flex-1 gap-3 sm:gap-4 p-3 sm:p-4 lg:p-5 items-start">
          <GetStartedPanel />
          <RecentChats />
        </main>
      </div>
    </>
  );
};

export default ConvoAi;